import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    CreditCard,
    Landmark,
    Users,
    FileText,
    Banknote,
    Receipt,
    CheckCircle2,
    Circle,
    ArrowRight,
    Download,
    Sparkles,
    Rocket,
} from "lucide-react";

/**
 * Guided Setup Wizard.
 *
 * Reads live counts to know which steps are already complete — no separate
 * "wizard state" collection needed. Each step tells the user what to add
 * and links to the appropriate module. Progress percentage is derived.
 */
const STEPS = [
    { key: "cards", label: "Add your credit cards", desc: "Start with your daily-driver cards. You can bulk-import via Excel below.", to: "/cards/new", icon: CreditCard, endpoint: "/cards", min: 1 },
    { key: "banks", label: "Add bank accounts", desc: "For payments and balances.", to: "/bank-accounts", icon: Landmark, endpoint: "/bank-accounts", min: 1 },
    { key: "holders", label: "Add card holders", desc: "Optional. Add family or team members using the cards.", to: "/card-holders", icon: Users, endpoint: "/card-holders", min: 0 },
                    { key: "statement", label: "Upload your first statement", desc: "PDF from your bank — we&rsquo;ll OCR the transactions.", to: "/statements", icon: FileText, endpoint: "/statements", min: 1 },
    { key: "opening", label: "Set opening outstanding balance", desc: "Optional. Enter the balance on each card as of your start date.", to: "/cards", icon: Banknote, endpoint: "/cards", min: 0, checkFn: (list) => list.some((c) => c.opening_outstanding) },
    { key: "expense", label: "Log your first expense", desc: "One-tap quick entry works on mobile too.", to: "/quick-entry", icon: Receipt, endpoint: "/expenses", min: 1 },
    { key: "payment", label: "Record your first payment", desc: "This is how outstanding balances get updated.", to: "/payments", icon: Banknote, endpoint: "/payments", min: 1 },
];

export default function SetupWizard() {
    const [counts, setCounts] = useState({});
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    async function load() {
        setLoading(true);
        try {
            const [c, t] = await Promise.all([
                Promise.all(STEPS.map((s) => api.get(s.endpoint).catch(() => ({ data: [] })))),
                api.get("/import/templates").catch(() => ({ data: [] })),
            ]);
            const next = {};
            STEPS.forEach((s, idx) => {
                const list = Array.isArray(c[idx].data) ? c[idx].data : [];
                const passed = s.checkFn ? s.checkFn(list) : list.length >= (s.min || 1);
                next[s.key] = { count: list.length, done: s.min === 0 ? true : passed };
            });
            setCounts(next);
            setTemplates(Array.isArray(t.data) ? t.data : []);
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const done = STEPS.filter((s) => counts[s.key]?.done).length;
    const pct = STEPS.length === 0 ? 0 : Math.round((done / STEPS.length) * 100);

    async function downloadTemplate(kind, filename) {
        try {
            const r = await api.get(`/import/templates/${kind}/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement("a");
            a.href = url; a.download = filename; a.click();
            window.URL.revokeObjectURL(url);
            toast.success(`${filename} downloaded`);
        } catch { toast.error("Download failed"); }
    }

    // ── Usage-type Q (persona nudge) ────────────────────────────
    // Persisted to localStorage so we don't spam the user across sessions.
    // Downstream: Home + sidebar read this to hide irrelevant modules
    // (e.g. Cash Desk for Personal, CA workspaces for Business).
    const [persona, setPersona] = useState(() => {
        try { return localStorage.getItem("vault_usage_type") || ""; } catch { return ""; }
    });
    const setPersonaValue = (v) => {
        setPersona(v);
        try { localStorage.setItem("vault_usage_type", v); } catch { /* noop */ }
    };

    return (
        <div className="max-w-[1000px] mx-auto" data-testid="setup-wizard-page">
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold mb-1 dark:text-stone-400">
                    Getting Started
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-stone-900 tracking-tight dark:text-white">
                    Set up VAULT in a few minutes
                </h1>
                <p className="text-sm text-stone-500 mt-1 dark:text-stone-400">
                    Follow the checklist below. Skip anything you don&rsquo;t need — you can add it later.
                </p>
            </div>

            {/* Persona picker — one question that personalises the whole app */}
            {!persona && (
                <div className="mb-6 bg-white border border-stone-200 rounded-2xl p-5 dark:bg-stone-900 dark:border-stone-800" data-testid="setup-persona-card">
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Tell us how you&apos;ll use VAULT</div>
                    <p className="text-sm text-stone-600 dark:text-stone-300 mb-4">One tap — we&apos;ll hide the modules you don&apos;t need.</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {[
                            { id: "personal", label: "Personal", desc: "Just me", icon: "👤" },
                            { id: "family",   label: "Family",   desc: "Household finance", icon: "👨‍👩‍👧" },
                            { id: "business", label: "Business", desc: "Small team", icon: "💼" },
                            { id: "ca",       label: "CA Practice", desc: "Multiple clients", icon: "📊" },
                        ].map((p) => (
                            <button
                                key={p.id}
                                onClick={() => setPersonaValue(p.id)}
                                className="text-left border border-stone-200 rounded-xl p-3 hover:border-stone-900 transition-colors dark:border-stone-700 dark:hover:border-stone-100"
                                data-testid={`setup-persona-${p.id}`}
                            >
                                <div className="text-2xl mb-1">{p.icon}</div>
                                <div className="text-sm font-semibold text-stone-900 dark:text-white">{p.label}</div>
                                <div className="text-[11px] text-stone-500">{p.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {persona && (
                <div className="mb-4 inline-flex items-center gap-2 text-[12px] text-stone-500" data-testid="setup-persona-badge">
                    Setup tuned for <span className="font-semibold text-stone-800 capitalize dark:text-stone-200">{persona}</span>
                    <button onClick={() => setPersonaValue("")} className="underline hover:text-stone-900">change</button>
                </div>
            )}

            {/* Progress card */}
            <div className="bg-stone-900 text-white rounded-2xl p-5 mb-6 dark:bg-stone-800">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-stone-400 font-semibold">Setup progress</div>
                        <div className="text-2xl font-bold mt-1">{done} of {STEPS.length} steps · {pct}%</div>
                    </div>
                    <Rocket size={32} className="text-amber-400" />
                </div>
                <div className="w-full bg-stone-700 rounded-full h-2 overflow-hidden">
                    <div
                        className="h-2 bg-amber-400 transition-all duration-500"
                        style={{ width: `${pct}%` }}
                        data-testid="setup-progress-bar"
                    />
                </div>
                {pct === 100 && (
                    <div className="mt-3 flex items-center gap-2 text-emerald-400 text-[13px]">
                        <Sparkles size={14} /> You&rsquo;re all set. Command Centre is ready.
                    </div>
                )}
            </div>

            {/* Step list */}
            <div className="space-y-2 mb-8" data-testid="setup-steps">
                {STEPS.map((s, idx) => {
                    const state = counts[s.key] || {};
                    const done = state.done;
                    return (
                        <div
                            key={s.key}
                            className={`bg-white dark:bg-stone-900 border rounded-2xl p-4 flex items-center gap-4 ${
                                done ? "border-emerald-200 dark:border-emerald-900" : "border-stone-200 dark:border-stone-800"
                            }`}
                            data-testid={`setup-step-${s.key}`}
                        >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                done ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-300" : "bg-stone-100 text-stone-400 dark:bg-stone-800"
                            }`}>
                                {done ? <CheckCircle2 size={16} /> : <span className="text-sm font-semibold">{idx + 1}</span>}
                            </div>
                            <s.icon size={18} className="text-stone-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-stone-900 dark:text-white">{s.label}</div>
                                <div className="text-[12px] text-stone-500 dark:text-stone-400 mt-0.5">{s.desc}</div>
                                {state.count > 0 && (
                                    <div className="text-[11px] text-stone-400 mt-1">{state.count} record(s)</div>
                                )}
                            </div>
                            <button
                                onClick={() => navigate(s.to)}
                                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold inline-flex items-center gap-1 shrink-0 ${
                                    done ? "border border-stone-200 text-stone-600 dark:border-stone-700 dark:text-stone-300" : "bg-stone-900 text-white hover:bg-stone-700 dark:bg-white dark:text-stone-900"
                                }`}
                                data-testid={`setup-open-${s.key}`}
                            >
                                {done ? "Open" : "Start"} <ArrowRight size={11} />
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Excel imports */}
            <section className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-2xl p-5">
                <h2 className="text-lg font-bold text-stone-900 dark:text-white">Bulk import templates</h2>
                <p className="text-sm text-stone-500 dark:text-stone-400 mt-1 mb-4">
                    Download a CSV template, fill it in Excel or Google Sheets, then upload from the relevant module.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {templates.map((tpl) => (
                        <button
                            key={tpl.kind}
                            onClick={() => downloadTemplate(tpl.kind, tpl.filename)}
                            className="text-left bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl p-4 hover:border-stone-300 transition-colors"
                            data-testid={`template-${tpl.kind}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="text-sm font-semibold text-stone-900 dark:text-white capitalize">{tpl.kind.replace("_", " ")}</div>
                                <Download size={14} className="text-stone-400" />
                            </div>
                            <div className="text-[11px] text-stone-500 dark:text-stone-400 mt-1">{tpl.columns} columns · {tpl.samples} sample row(s)</div>
                        </button>
                    ))}
                </div>
            </section>

            {loading && (
                <div className="text-center mt-4 text-sm text-stone-500">Loading progress…</div>
            )}
            <div className="mt-6 text-center">
                <Link to="/dashboard" className="text-sm text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white" data-testid="setup-back-home">
                    ← Back to Command Centre
                </Link>
            </div>
        </div>
    );
}
